// Google Ads conversion tag — browser gtag.js, marketing site only.
//
// Deliberately built as a near-mirror of `metaPixel.js`: same env-var gating,
// same "unset ID means every export is a clean no-op" contract, same init guard,
// same marketing-path scoping (it reuses `isMarketingPath` from metaPixel rather
// than defining a second copy that could drift out of lockstep with App.js's
// `isNoindexRoute`). If you are changing one of these two files, check whether
// the other needs the same change.
//
// WHY THIS EXISTS: the Aug 9–17 2026 paid push runs Meta and Google Search ads
// to /trial. Meta already had a `Lead` event; Google had no tag on the site at
// all. This is that tag, and nothing more. The /trial page and its
// trial-booking conversion event were deleted on 2026-09-03 (phase over);
// the base tag stays so a future conversion has attribution to stand on.
//
// SHIPPED DARK on 2026-08-08 with neither env var set, and SINCE ACTIVATED.
// Verified against production 2026-08-11: both vars are set, both BYTE-MATCH
// the values Google Ads reports for the "Trial booking" conversion action
// (`AW-18379083956` / label `txY7CKyvw94cELSh6rtE`), both are baked into the
// served bundle, and `gtag` loads and fires on the live /trial page — checked
// in a real browser, not inferred from the source. The no-op contract below
// still holds if either var is ever unset again.
//
// WHAT "LIVE" DOES NOT MEAN, because the console says otherwise and it reads
// like a defect: Google reports the conversion action as **Inactive** and its
// parent goal as **Misconfigured** until a real booking fires it. That is zero
// data, not a broken tag. It flips on the first conversion. A session was spent
// chasing this — don't repeat it.
//
// SCOPE — what this deliberately does NOT do:
//   • No CAPI / server-side conversions. Deferred past Aug 31 by decision.
//   • No gclid capture into any callable payload. gtag.js sets its own
//     first-party `_gcl_aw` cookie from the landing URL and handles attribution
//     itself, which is exactly why init runs on marketing page views and not
//     only at conversion time — see initGoogleAds below.

import { isMarketingPath } from './metaPixel';

// CRA convention, mirrors REACT_APP_META_PIXEL_ID / REACT_APP_FIREBASE_*.
// ADS_ID looks like "AW-1234567890". (REACT_APP_GOOGLE_ADS_TRIAL_LABEL, the
// per-conversion label for the Aug 2026 trial booking, went with the /trial
// page on 2026-09-03 and is no longer read.)
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID;

// Records whether the base gtag.js snippet has been injected. gtag.js cannot be
// unloaded once in the document, so this only ever goes false → true.
let initialized = false;

// True once gtag.js has been injected and `gtag` is callable.
export function isGoogleAdsReady() {
	return initialized && typeof window !== 'undefined' && typeof window.gtag === 'function';
}

// Inject gtag.js and configure the Ads account. Idempotent, and a clean no-op
// without a configured ID.
//
// This runs on marketing PAGE VIEWS, not only at conversion time, and that is
// load-bearing rather than sloppy: a Google Search ad lands on /trial carrying a
// `?gclid=…`, and gtag.js is what reads that parameter and writes it to the
// first-party `_gcl_aw` cookie. If the tag only loaded at the moment of booking,
// the gclid would already be gone from the URL — the SPA has navigated since —
// and the conversion would fire attributed to nothing. Loading at view time is
// what makes the conversion traceable back to the click WITHOUT us capturing the
// gclid ourselves, which would have meant changing the `bookTrialSlot` payload
// and therefore a Cloud Functions deploy this whole change exists to avoid.
export function initGoogleAds() {
	if (initialized) return;
	if (!ADS_ID) return; // no ID configured → no-op, nothing injected
	if (typeof window === 'undefined' || typeof document === 'undefined') return;

	const s = document.createElement('script');
	s.async = true;
	s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ADS_ID)}`;
	document.head.appendChild(s);

	// The standard gtag bootstrap. `gtag` pushes raw `arguments` onto dataLayer
	// and MUST NOT be an arrow function or a (...args) rest wrapper — gtag.js
	// reads the arguments object itself, and a rewritten call signature is the
	// classic silent-failure mode here.
	window.dataLayer = window.dataLayer || [];
	function gtag() {
		// eslint-disable-next-line prefer-rest-params
		window.dataLayer.push(arguments);
	}
	window.gtag = gtag;

	window.gtag('js', new Date());
	window.gtag('config', ADS_ID);

	initialized = true;
}

// Called on every marketing route change, alongside the Meta pixel's PageView.
// Google Ads conversion tracking needs no per-view event of its own — `config`
// above already reports the page — so this exists only to guarantee the tag is
// present early enough to catch the gclid.
export function trackGoogleAdsPageView(pathname) {
	if (!isMarketingPath(pathname)) return;
	initGoogleAds();
}

